import React from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-react-intl"
import styled, { css } from "styled-components"

import { Container, Row, Col } from "../components/Grid"
import Link from "../components/Link"
import { TextButton } from "../components/Button"
import { showConsentManager } from "../helpers/tracking"

import { media } from "../styles"
import LogoSVG from "../images/logos/solaris-logo.inline.svg"
import YoutubeSVG from "../images/icons/youtube.inline.svg"
import LinkedinSVG from "../images/icons/linkedin.inline.svg"

const FooterContainer = styled.div`
  background-color: ${({ theme }) => theme.spaceblue};
  color: ${({ theme }) => theme.white};
  font-size: 15px;
  line-height: 1.35;
  padding: 50px 0 100px;
  text-align: center;

  @media ${media.sm} {
    font-size: 14px;
    text-align: left;
  }

  @media ${media.md} {
    padding: 60px 0 100px;
  }

  @media print {
    background: white;
    padding: 0;
  }

  p {
    font-size: inherit;
  }
`

const FooterRow = styled(Row)`
  margin-bottom: 30px;

  @media ${media.sm} {
    margin-bottom: 0;
  }

  @media ${media.lg} {
    margin-bottom: 30px;
  }

  @media print {
    display: none;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const FooterLogo = styled.div`
  color: ${({ theme }) => theme.greyLight};
  height: 20px;
  margin: 0 auto 40px;
  width: 96px;

  @media ${media.sm} {
    height: 22px;
    margin-left: 0;
    width: 106px;
  }

  @media ${media.md} {
    margin-bottom: 60px;
  }

  @media print {
    color: ${({ theme }) => theme.spaceblue};
  }

  a {
    color: inherit;
  }

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }
`

const GroupHeader = styled.h3`
  font-size: inherit;
  font-weight: 700;
  line-height: inherit;
  margin: 0 0 12px;

  @media ${media.sm} {
    margin: 0 0 8px;
  }
`

const NavList = styled.ul`
  list-style: none;
  margin: 0 0 40px;
  padding: 0;

  @media ${media.sm} {
    margin: 0 0 50px;
  }
`

const NavItem = styled.li`
  margin: 0 0 12px;
  padding: 0;

  @media ${media.sm} {
    margin: 0 0 8px;
  }
`

const NavLinkStyles = css`
  color: ${({ theme }) => theme.sage};
  display: inline-block;
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitionTime};

  &:hover {
    color: ${({ theme }) => theme.white};
  }

  .link-icon {
    max-height: 10px;
    width: auto;
    margin-left: 5px;
  }
`

const NavLink = styled(Link)`
  ${NavLinkStyles}
`

const NavButton = styled(TextButton)`
  ${NavLinkStyles}
`

const MetaRow = styled.div`
  display: flex;
  flex-direction: column;

  @media ${media.lg} {
    flex-flow: row nowrap;
  }
`

const MetaList = styled.div`
  display: flex;
  flex-flow: column;
  list-style: none;
  margin: 0 0 40px;
  padding: 0;

  @media ${media.sm} {
    flex-flow: row wrap;
  }

  @media ${media.md} {
    flex-flow: row nowrap;
  }

  @media ${media.lg} {
    margin: 0;
  }

  @media ${media.xl} {
    flex-flow: row nowrap;
  }
`

const MetaItem = styled.span`
  margin: 0 0 12px;

  @media ${media.sm} {
    margin: 0 20px 0 0;

    &:last-child {
      margin-right: 0;
    }
  }

  @media ${media.md} {
    margin-right: 20px;
  }

  @media ${media.lg} {
    margin-right: 30px;
  }

  @media ${media.xl} {
    white-space: nowrap;
  }

  @media print {
    display: none;
  }

  ${NavLink}, ${NavButton} {
    color: ${({ theme }) => theme.white};

    &:hover {
      color: ${({ theme }) => theme.sage};
    }

    .link-icon {
      vertical-align: -1px;
    }
  }
`

const Copyright = styled(MetaItem)`
  color: ${({ theme }) => theme.sage};
  order: 1;
  width: 100%;

  @media ${media.sm} {
    order: 0;
    margin: 0 0 40px;
  }

  @media ${media.md} {
    margin: 0 20px 0 0;
    width: auto;
  }

  @media ${media.lg} {
    margin: 0 40px 0 0;
  }

  @media ${media.xl} {
    width: auto;
  }

  @media print {
    display: block;
  }
`

const SocialList = styled.ul`
  display: flex;
  flex-flow: row nowrap;
  list-style: none;
  margin: 0 auto;
  padding: 0;

  @media ${media.md} {
    margin: 0;
  }

  @media ${media.lg} {
    margin: 0 0 0 auto;
    width: auto;
  }
`

const SocialListItem = styled.li`
  margin: 0 20px;

  @media ${media.md} {
    margin: 0 40px 0 0;
  }

  @media ${media.lg} {
    margin: 0 0 0 40px;

    &:first-child {
      margin-left: 0;
    }
  }
`

const SocialLink = styled.a`
  color: ${({ theme }) => theme.white};
  transition: color ${({ theme }) => theme.transitionTime};

  &:hover {
    color: ${({ theme }) => theme.sage};
  }
`

const SocialIcon = styled.div``

const SocialIcons = {
  youtube: YoutubeSVG,
  linkedin: LinkedinSVG,
}

const Compliance = styled.div`
  border-bottom: 1px solid ${({ theme }) => `rgba(${theme.whiteRGB},0.15)`};
  border-top: 1px solid ${({ theme }) => `rgba(${theme.whiteRGB},0.15)`};
  margin: 0 0 40px;
  padding: 30px 0 13px;
`

const Footer = ({ forceLocale, translations }) => {
  const intl = useIntl()

  const data = useStaticQuery(graphql`
    query FooterQuery {
      site {
        siteMetadata {
          title
          supportPhone
          supportEmail
          supportUrl
          socialProfiles {
            name
            url
            logoId
            includeInFooter
          }
        }
      }
      allFooterJson {
        nodes {
          id
          locale
          groups {
            headline
            items {
              label
              slug
              target
            }
          }
          meta {
            address
            copyright
            items {
              label
              slug
              target
            }
          }
          compliance {
            items {
              label
              name
              role
              slug
              target
            }
          }
        }
      }
    }
  `)

  // If we are forcing a locale, use it (e.g. localized landing pages, which aren't part of the regular bilingual pages).
  // Otherwise, fall back to the locale provided by useIntl()
  const footerData =
    forceLocale &&
    data.allFooterJson.nodes.find((node) => node.locale === forceLocale)
      ? data.allFooterJson.nodes.find((node) => node.locale === forceLocale)
      : data.allFooterJson.nodes.find((node) => node.locale === intl.locale)

  return (
    <FooterContainer data-testid="footer">
      <Container>
        <Row>
          <Col xl="10" xlOffset="1">
            <FooterLogo>
              <Link to="/" title={data.site.siteMetadata.title}>
                <LogoSVG />
              </Link>
            </FooterLogo>

            <FooterRow xlJustifyContent="between">
              {footerData.groups.map((group) => (
                <Col
                  col="12"
                  sm="6"
                  md="4"
                  lg="3"
                  xl="auto"
                  order="0"
                  key={group.headline}
                >
                  <GroupHeader>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: group.headline,
                      }}
                    />
                  </GroupHeader>
                  <NavList>
                    {group.items.map((item) => (
                      <NavItem key={item.label}>
                        <NavLink
                          intl={false}
                          to={item.slug}
                          target={item.target}
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html: item.label,
                            }}
                          />
                        </NavLink>
                      </NavItem>
                    ))}
                  </NavList>
                </Col>
              ))}
              <Col col="12" order="1" smOrder="2" xlOrder="1">
                <Compliance>
                  {footerData.compliance.items.map((item, i) => (
                    <p key={i}>
                      <strong>{item.role}:</strong> <nobr>{item.name}</nobr>{" "}
                      &nbsp;&nbsp;
                      <NavLink
                        intl={false}
                        to={item.slug}
                        target={item.target}
                        arrow
                      >
                        {item.label}
                      </NavLink>
                      <br />
                    </p>
                  ))}
                </Compliance>
              </Col>
              <Col
                col="12"
                sm="6"
                md="4"
                lg="9"
                lgOffset="0"
                xl="12"
                xlOffset="0"
                order="2"
                smOrder="1"
                xlOrder="2"
              >
                <Row>
                  <Col col="12" lg="5" xl="3">
                    <p
                      dangerouslySetInnerHTML={{
                        __html: footerData.meta.address,
                      }}
                    />
                  </Col>
                  <Col col="12" lg="7">
                    <p>
                      <strong>
                        {translations?.["footer.support"] ||
                          intl.formatMessage({ id: "footer.support" })}
                      </strong>
                      <br />
                      Tel:{" "}
                      <MetaItem>
                        <NavLink
                          intl={false}
                          to={`tel:${data.site.siteMetadata.supportPhone}`}
                        >
                          {data.site.siteMetadata.supportPhone}
                        </NavLink>
                      </MetaItem>
                      <br />
                      <MetaItem>
                        <NavLink
                          intl={false}
                          to={`${data.site.siteMetadata.supportUrl}/hc/${
                            intl.locale === "de" ? "de" : "en-us"
                          }`}
                          arrow
                        >
                          {translations?.["footer.supportCenter"] ||
                            intl.formatMessage({
                              id: "footer.supportCenter",
                            })}
                        </NavLink>
                      </MetaItem>
                    </p>
                  </Col>
                </Row>
              </Col>
            </FooterRow>

            <MetaRow>
              <MetaList>
                <Copyright>{footerData.meta.copyright}</Copyright>
                {footerData.meta.items.map((item) => (
                  <MetaItem key={item.label}>
                    <NavLink intl={false} to={item.slug} target={item.target}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: item.label,
                        }}
                      />
                    </NavLink>
                  </MetaItem>
                ))}
                <MetaItem>
                  <NavButton onClick={showConsentManager}>
                    {translations?.["footer.consentManager"] ||
                      intl.formatMessage({ id: "footer.consentManager" })}
                  </NavButton>
                </MetaItem>
              </MetaList>
              <SocialList>
                {data.site.siteMetadata.socialProfiles.map(
                  (profile) =>
                    profile.includeInFooter && (
                      <SocialListItem key={profile.name}>
                        <SocialLink
                          href={profile.url}
                          title={profile.name}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <SocialIcon as={SocialIcons[profile.logoId]} />
                        </SocialLink>
                      </SocialListItem>
                    )
                )}
              </SocialList>
            </MetaRow>
          </Col>
        </Row>
      </Container>
    </FooterContainer>
  )
}

Footer.propTypes = {
  forceLocale: PropTypes.string,
}

export default Footer
